/**
 * PATH: packages/auth/src/reload-watchdog/installReloadInstrumentation.ts
 * FILE_ID: AUTH-RELOAD-WATCHDOG-INSTALL-001
 * PURPOSE: Wrap window.location.{reload,replace,assign} on the host
 *          SPA so every reload attempt is (a) logged to the console
 *          with a full stack + pathname trail, (b) recorded in a
 *          sessionStorage trail that survives across reloads, (c)
 *          posted to /api/diagnostics/client-error/ for server-side
 *          inspection, and (d) routed through the reload-circuit-
 *          breaker so a runaway loop is suppressed at the third
 *          bounce inside a 10-second window.
 * WHY:     Tenant SPAs were shipping without ANY reload-loop
 *          defence. Operator 2026-05-24: *"the job page refreshed
 *          like 12 times before stabalising after a refresh again..
 *          this is a recurring problem pattern audit and fix it
 *          also put proper trails to see when the refresh happens
 *          so you understand the trigger leading to it"*. The
 *          master SPA had the trail + breaker since 2026-05-21
 *          (`src/main.jsx` lines 38-305); this module is the
 *          shared, importable version of that wrapper so every
 *          tenant SPA can install it from its `main.tsx` with a
 *          single import.
 *
 *          What this module does NOT do: it does not stop reloads
 *          from happening on the FIRST loop iteration. The trail
 *          + diagnostic POST are designed to make the trigger
 *          observable; the breaker stops the loop on attempt 4+
 *          inside a 10s window so the user isn't trapped in a
 *          strobing screen. To stop reloads earlier, the trigger
 *          itself must be fixed (the trail tells us which trigger).
 * FLOW:
 *   installReloadInstrumentation({ tenantTag, buildSha })
 *     → wrap window.location.reload  → recordReloadAttempt → suppress
 *     → wrap window.location.replace → log + diagnostic POST (no break)
 *     → wrap window.location.assign  → log + diagnostic POST (no break)
 *     → if already-tripped on boot: render banner immediately so the
 *       operator sees why the page is "stuck".
 *
 * DEPENDENCIES:
 *   - ./reloadCircuitBreaker: the per-tab breaker logic.
 *   - global `fetch` for the diagnostic POST (best-effort, never blocks).
 *   - global `document` + `sessionStorage` (guarded by typeof checks).
 *
 * RELATED:
 *   - src/main.jsx lines 38-305: master SPA's inline equivalent.
 *     Byte-equivalent contract (a future MR can have master re-export
 *     from this module).
 */

import {
  recordReloadAttempt,
  resetReloadBreaker,
  getLastReloadReason,
  isReloadBreakerTripped,
  getReloadTrail,
  type ReloadTrailEntry,
} from './reloadCircuitBreaker'

/** Options passed by each tenant SPA's main entry. */
export interface InstallReloadInstrumentationOptions {
  /** Free-form tag identifying the host SPA (`'sam'`, `'newmark'`,
   *  `'steel'`, `'developer'`, `'workforce'`, `'master'`). Surfaced
   *  in the diagnostic POST + console logs so backend support can
   *  tell which SPA fired the reload. */
  tenantTag: string
  /** The VITE_BUILD_SHA the host SPA was built with. Surfaced in
   *  the diagnostic POST so support can correlate the reload with
   *  a specific bundle. */
  buildSha?: string | null
  /** Endpoint to POST diagnostics to. Defaults to
   *  `/api/diagnostics/client-error/` which all SPAs share. */
  diagnosticsEndpoint?: string
  /** Override the global console for tests. */
  console?: Pick<Console, 'warn' | 'error' | 'info'>
  /** Best-effort active-organisation id accessor. The bare reload POST
   *  carries no Authorization header, so without an org hint the BE can
   *  only ever store the row as org=NULL (unfindable per operator). Pass
   *  `getOrganisationIdForDiagnostics` from @quotech/auth. */
  getOrganisationId?: () => number | string | null | undefined
}

function _stamp(): string {
  return new Date().toISOString()
}

function _postDiagnostic(
  endpoint: string,
  payload: Record<string, unknown>,
  { keepalive = false }: { keepalive?: boolean } = {},
): void {
  // Fire-and-forget. Never block reload (or its suppression).
  // `keepalive` lets the beacon outlive an imminent navigation: the
  // ALLOWED-reload path POSTs immediately before window.location.reload()
  // tears the page down, so without it the browser cancels the in-flight
  // POST and the refresh trail is lost.
  try {
    if (typeof fetch === 'undefined') return
    void fetch(endpoint, {
      method: 'POST',
      credentials: 'include',
      keepalive,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        kind: 'reload-circuit-breaker',
        ...payload,
      }),
    }).catch(() => { /* ignore */ })
  } catch { /* ignore */ }
}

function _renderBanner(reason: string, trail: ReloadTrailEntry[]): void {
  try {
    if (typeof document === 'undefined') return
    if (document.getElementById('quotech-reload-breaker-banner')) return
    if (!document.body) {
      const waitForBody = () => {
        if (document.body) _renderBanner(reason, trail)
        else setTimeout(waitForBody, 50)
      }
      waitForBody()
      return
    }
    const banner = document.createElement('div')
    banner.id = 'quotech-reload-breaker-banner'
    banner.style.cssText = [
      'position:fixed', 'top:0', 'left:0', 'right:0', 'z-index:2147483647',
      'background:linear-gradient(135deg,#fef2f2,#fee2e2)',
      'border-bottom:2px solid #dc2626',
      'color:#7f1d1d', 'padding:14px 18px',
      'font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif',
      'font-size:13px', 'line-height:1.45',
      'box-shadow:0 4px 16px rgba(0,0,0,0.18)',
    ].join(';')
    const safeReason = (reason || '').toString().slice(0, 2000)
    const trailLines = trail.slice(-12).map((e, i) =>
      `${i + 1}. [${new Date(e.at).toISOString()}] ${e.pathname} → ${e.outcome} (count=${e.count}) — ${e.reason.slice(0, 200)}`,
    ).join('\n')
    banner.innerHTML = `
      <div style="max-width:1100px;margin:0 auto;display:flex;gap:12px;align-items:flex-start">
        <div style="flex-shrink:0;font-size:18px">⛔</div>
        <div style="flex:1;min-width:0">
          <div style="font-weight:600;margin-bottom:4px">
            Page refresh suppressed — reload loop detected
          </div>
          <div style="font-size:12px;color:#991b1b;margin-bottom:8px">
            We stopped the page from reloading again because it was
            refreshing repeatedly. The chain of triggers is recorded below —
            copy it to support if this keeps happening.
          </div>
          <details style="font-size:11px;font-family:ui-monospace,SF Mono,Consolas,monospace;background:rgba(255,255,255,0.7);border:1px solid #fecaca;border-radius:4px;padding:6px 8px;max-height:240px;overflow:auto" open>
            <summary style="cursor:pointer;color:#7f1d1d;font-weight:500">Reload trail (last ${Math.min(trail.length, 12)} of ${trail.length})</summary>
            <pre style="white-space:pre-wrap;word-break:break-all;margin:6px 0 0 0;color:#7f1d1d" id="quotech-reload-breaker-trail"></pre>
          </details>
          <details style="font-size:11px;font-family:ui-monospace,SF Mono,Consolas,monospace;background:rgba(255,255,255,0.7);border:1px solid #fecaca;border-radius:4px;padding:6px 8px;max-height:160px;overflow:auto;margin-top:6px">
            <summary style="cursor:pointer;color:#7f1d1d;font-weight:500">Last reload reason (with stack)</summary>
            <pre style="white-space:pre-wrap;word-break:break-all;margin:6px 0 0 0;color:#7f1d1d" id="quotech-reload-breaker-reason"></pre>
          </details>
          <div style="margin-top:10px;display:flex;gap:8px;flex-wrap:wrap">
            <button id="quotech-reload-breaker-force"
              style="background:#dc2626;color:#fff;border:0;padding:6px 14px;border-radius:4px;font-size:12px;font-weight:500;cursor:pointer">
              Force refresh anyway
            </button>
            <a href="/"
              style="background:transparent;color:#7f1d1d;border:1px solid #fca5a5;padding:6px 14px;border-radius:4px;font-size:12px;font-weight:500;cursor:pointer;text-decoration:none">
              Sign out
            </a>
            <button id="quotech-reload-breaker-dismiss"
              style="background:transparent;color:#7f1d1d;border:0;padding:6px 8px;font-size:12px;cursor:pointer;margin-left:auto">
              Dismiss banner
            </button>
          </div>
        </div>
      </div>
    `
    document.body.prepend(banner)
    const reasonNode = document.getElementById('quotech-reload-breaker-reason')
    if (reasonNode) reasonNode.textContent = safeReason
    const trailNode = document.getElementById('quotech-reload-breaker-trail')
    if (trailNode) trailNode.textContent = trailLines || '<trail empty>'
    document.getElementById('quotech-reload-breaker-force')?.addEventListener('click', () => {
      try { resetReloadBreaker() } catch { /* ignore */ }
      try { window.location.reload() } catch { /* ignore */ }
    })
    document.getElementById('quotech-reload-breaker-dismiss')?.addEventListener('click', () => {
      try { banner.remove() } catch { /* ignore */ }
    })
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[reload-watchdog] failed to render banner:', err)
  }
}

/**
 * Install the reload instrumentation on the current tab. Idempotent
 * within a tab — calling twice is a no-op (we tag the location
 * object with a sentinel). Call once at SPA boot, before any
 * reload trigger has fired.
 */
export function installReloadInstrumentation(
  opts: InstallReloadInstrumentationOptions,
): void {
  const { tenantTag, buildSha, diagnosticsEndpoint = '/api/diagnostics/client-error/', getOrganisationId } = opts
  const log = opts.console ?? console
  if (typeof window === 'undefined') return

  const _safeOrgId = (): number | string | undefined => {
    try {
      const v = getOrganisationId?.()
      return v == null ? undefined : v
    } catch {
      return undefined
    }
  }

  // Idempotency guard. Some tenants might call this from multiple
  // mount points during a refactor; only install once per tab.
  const SENTINEL = '__quotech_reload_watchdog_installed__'
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((window as any)[SENTINEL]) return
  try {
    Object.defineProperty(window, SENTINEL, { value: true, writable: false, configurable: false })
  } catch {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(window as any)[SENTINEL] = true
  }

  try {
    const originalReload = window.location.reload.bind(window.location)
    const originalReplace = window.location.replace.bind(window.location)
    const originalAssign = window.location.assign.bind(window.location)

    const tagReload = (...args: unknown[]) => {
      const stamp = _stamp()
      const stack = new Error('reload-trace').stack || '<no-stack>'
      const reasonForBanner =
        `[${tenantTag}] [reload-diagnostic ${stamp}] window.location.reload() called.\n` +
        `pathname=${window.location.pathname} search=${window.location.search}\n` +
        `args=${JSON.stringify(args)}\n` +
        `stack:\n${stack}`
      try { log.warn(reasonForBanner) } catch { /* never block reload */ }

      let verdict
      try {
        verdict = recordReloadAttempt(reasonForBanner)
      } catch (err) {
        log.warn('[reload-watchdog] verdict threw, allowing reload:', err)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (originalReload as any)(...args)
      }

      if (!verdict.allowed) {
        if (verdict.suppressed) {
          log.error(
            `[reload-watchdog] TRIPPED (count=${verdict.count}). ` +
            `Suppressing reload. Original cause:\n${reasonForBanner}`,
          )
          _postDiagnostic(diagnosticsEndpoint, {
            tenantTag,
            buildSha: buildSha ?? null,
            organisation_id: _safeOrgId(),
            stamp,
            count: verdict.count,
            suppressed: true,
            pathname: window.location.pathname,
            search: window.location.search,
            reason: reasonForBanner,
            trail: getReloadTrail(),
          })
          _renderBanner(reasonForBanner, getReloadTrail())
        }
        return undefined
      }
      // Allowed — but still POST a lightweight trail entry so the
      // server-side log captures the reload BEFORE it happens. This
      // is the audit trail the operator asked for. keepalive so it
      // survives the navigation we're about to perform.
      _postDiagnostic(
        diagnosticsEndpoint,
        {
          tenantTag,
          buildSha: buildSha ?? null,
          organisation_id: _safeOrgId(),
          stamp,
          count: verdict.count,
          suppressed: false,
          pathname: window.location.pathname,
          search: window.location.search,
          reason: reasonForBanner,
        },
        { keepalive: true },
      )
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (originalReload as any)(...args)
    }

    const tagReplace = (url: string | URL) => {
      try {
        log.warn(
          `[${tenantTag}] [reload-diagnostic ${_stamp()}] window.location.replace(${JSON.stringify(String(url))}) called.\n` +
          `pathname=${window.location.pathname} search=${window.location.search}\n` +
          `stack:\n${new Error('replace-trace').stack}`,
        )
      } catch { /* never block redirect */ }
      return originalReplace(url)
    }

    const tagAssign = (url: string | URL) => {
      try {
        log.warn(
          `[${tenantTag}] [reload-diagnostic ${_stamp()}] window.location.assign(${JSON.stringify(String(url))}) called.\n` +
          `pathname=${window.location.pathname} search=${window.location.search}\n` +
          `stack:\n${new Error('assign-trace').stack}`,
        )
      } catch { /* never block redirect */ }
      return originalAssign(url)
    }

    // INCIDENT 2026-05-30 (Jake's 30× strobe with an empty trail):
    //   In some embedded / hardened browsers `window.location.reload`
    //   is a NON-WRITABLE accessor, so the assignment below throws and
    //   is swallowed — the wrapper silently never installs. Reloads
    //   then bypass the breaker entirely (native reload), so the loop
    //   strobed 30× and the breaker trail stayed empty (it was never
    //   consulted). We can't force the wrap to stick, so instead we
    //   VERIFY it stuck and publish the verdict on a window flag. Call
    //   sites that trigger reloads (e.g. tenant-shared's response
    //   identity guard) read this flag: when the wrapper is NOT live
    //   they run their OWN call-site breaker guard against the same
    //   sessionStorage keys so the loop still self-limits.
    let reloadWrapped = false
    try {
      window.location.reload = tagReload
      reloadWrapped = window.location.reload === tagReload
    } catch { reloadWrapped = false }
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(window as any).__quotech_reload_reload_wrapped__ = reloadWrapped
    } catch { /* ignore */ }
    try { window.location.replace = tagReplace } catch { /* ignore */ }
    try { window.location.assign = tagAssign } catch { /* ignore */ }
    log.info(
      `[reload-watchdog] instrumentation installed for tenant=${tenantTag} ` +
      `build=${buildSha ?? '<unknown>'} reloadWrapped=${reloadWrapped}`,
    )
    if (!reloadWrapped) {
      log.warn(
        '[reload-watchdog] window.location.reload is non-writable in this ' +
        'browser — the global reload breaker is INACTIVE. Reload triggers ' +
        'must self-limit at their call site (see tenant-shared quotechApi).',
      )
    }

    if (isReloadBreakerTripped()) {
      const lastReason = getLastReloadReason() || '<no recorded reason>'
      _renderBanner(
        `[reload-watchdog] Breaker tripped on a previous attempt. Last recorded reason:\n${lastReason}`,
        getReloadTrail(),
      )
    }
  } catch (err) {
    log.warn('[reload-watchdog] failed to install:', err)
  }
}
